import React, { useState } from 'react';
import { Eye, EyeOff, Wand2 } from 'lucide-react';
import { useToast } from '../../contexts/ToastContext';
import type { DipendenteStudio, SedeStudio } from '../../services/dipendentiService';
import { sedeDaValore, valoreSede } from '../../utils/sediDipendenti';
import {
  CODICI_SEZIONE,
  CodiceSezione,
  ETICHETTE_SEZIONE,
  SEZIONI_PREDEFINITE,
} from '../../utils/sezioniDipendente';

export type ModoModulo = 'nuovo' | 'sezioni' | 'password';

export interface ValoriModulo {
  nome?: string;
  cognome?: string;
  password?: string;
  sezioni?: string[];
  // solo nel nuovo accesso e solo con più sedi: id della sede (null = principale)
  sede?: string | null;
}

interface Props {
  modo: ModoModulo;
  dipendente?: DipendenteStudio | null;
  sezioniDisponibili?: string[];
  // sedi dello studio: con più di una, il nuovo accesso chiede la sede
  sedi?: SedeStudio[];
  onClose: () => void;
  // Promessa risolta a salvataggio riuscito; in errore il modulo resta aperto (il messaggio lo dà il genitore)
  onConferma: (valori: ValoriModulo) => Promise<void>;
}

const REGOLA_PASSWORD = 'Almeno 8 caratteri. Evita il nome, quello dello studio, la Partita IVA e le password comuni.';
// Senza caratteri che si confondono (0/O, 1/l/I): la password si detta anche a voce
const ALFABETO = 'abcdefghjkmnpqrstuvwxyzABCDEFGHJKLMNPQRSTUVWXYZ23456789';

/** Password casuale di 12 caratteri, con almeno una cifra. */
export function generaPassword(lunghezza = 12): string {
  const valori = new Uint32Array(lunghezza);
  if (typeof window !== 'undefined' && window.crypto?.getRandomValues) {
    window.crypto.getRandomValues(valori);
  } else {
    for (let i = 0; i < lunghezza; i++) valori[i] = Math.floor(Math.random() * 0xffffffff);
  }
  let out = Array.from(valori, (v) => ALFABETO[v % ALFABETO.length]).join('');
  if (!/\d/.test(out)) out = out.slice(0, -1) + ALFABETO[ALFABETO.length - 1 - (valori[0] % 8)];
  return out;
}

const TITOLI: Record<ModoModulo, string> = {
  nuovo: 'Aggiungi dipendente',
  sezioni: 'Sezioni abilitate',
  password: 'Reimposta la password',
};

const PULSANTI: Record<ModoModulo, string> = {
  nuovo: 'Crea accesso',
  sezioni: 'Salva sezioni',
  password: 'Reimposta',
};

/**
 * Modulo del titolare per un dipendente (spec dipendenti §7): nuovo accesso (nome, cognome, sede se lo
 * studio ne ha più di una, password con «Genera», sezioni), modifica delle sezioni, reimpostazione della password.
 */
const ModuloDipendente: React.FC<Props> = ({ modo, dipendente, sezioniDisponibili, sedi, onClose, onConferma }) => {
  const toast = useToast();
  const [nome, setNome] = useState('');
  const [cognome, setCognome] = useState('');
  const [password, setPassword] = useState('');
  const [mostra, setMostra] = useState(false);
  const [sezioni, setSezioni] = useState<string[]>(() =>
    modo === 'nuovo' ? [...SEZIONI_PREDEFINITE] : [...(dipendente?.sezioni || [])]
  );
  const [salvataggio, setSalvataggio] = useState(false);
  const conSede = modo === 'nuovo' && (sedi?.length || 0) > 1;
  // predefinita la principale (id null); se manca nell'elenco, la prima sede
  const [sede, setSede] = useState<string>(() => {
    const elenco = sedi || [];
    return valoreSede((elenco.find((s) => !s.id) || elenco[0])?.id);
  });

  const elencoSezioni = (sezioniDisponibili && sezioniDisponibili.length ? sezioniDisponibili : CODICI_SEZIONE) as string[];
  const conPassword = modo === 'nuovo' || modo === 'password';
  const conSezioni = modo === 'nuovo' || modo === 'sezioni';
  const nominativo = dipendente ? `${dipendente.nome} ${dipendente.cognome}`.trim() : '';

  const cambiaSezione = (codice: string) => {
    setSezioni((prima) => (prima.includes(codice) ? prima.filter((c) => c !== codice) : [...prima, codice]));
  };

  const invia = async (e: React.FormEvent) => {
    e.preventDefault();
    if (modo === 'nuovo' && (!nome.trim() || !cognome.trim())) {
      toast.error('Inserisci nome e cognome del dipendente');
      return;
    }
    if (conPassword && password.length < 8) {
      toast.error('La password deve avere almeno 8 caratteri');
      return;
    }
    const valori: ValoriModulo = {};
    if (modo === 'nuovo') {
      valori.nome = nome.trim();
      valori.cognome = cognome.trim();
    }
    if (conSede) valori.sede = sedeDaValore(sede);
    if (conPassword) valori.password = password;
    // ordine delle sezioni come nell'elenco del server
    if (conSezioni) valori.sezioni = elencoSezioni.filter((c) => sezioni.includes(c));
    setSalvataggio(true);
    try {
      await onConferma(valori);
    } catch (err) {
      // messaggio già mostrato dal genitore: il modulo resta aperto
    } finally {
      setSalvataggio(false);
    }
  };

  const inputClass = 'w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-indigo-500 outline-none';
  const labelClass = 'block text-xs font-medium text-gray-600 dark:text-gray-300 mb-1';

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" role="dialog" aria-modal="true" aria-labelledby="modulo-dipendente-titolo">
      <div className="absolute inset-0 bg-black/50" onClick={salvataggio ? undefined : onClose} aria-hidden="true" />
      <form onSubmit={invia} className="relative w-full max-w-lg max-h-[calc(100vh-2rem)] overflow-y-auto bg-white dark:bg-gray-900 rounded-xl shadow-2xl border border-gray-200 dark:border-gray-800 p-5">
        <h2 id="modulo-dipendente-titolo" className="text-base font-semibold text-gray-900 dark:text-white">{TITOLI[modo]}</h2>
        {nominativo && <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">{nominativo}</p>}

        <div className="mt-4 space-y-3">
          {modo === 'nuovo' && (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <label htmlFor="dipendente-nome" className={labelClass}>Nome</label>
                <input id="dipendente-nome" value={nome} onChange={(e) => setNome(e.target.value)} maxLength={60} autoComplete="off" className={inputClass} />
              </div>
              <div>
                <label htmlFor="dipendente-cognome" className={labelClass}>Cognome</label>
                <input id="dipendente-cognome" value={cognome} onChange={(e) => setCognome(e.target.value)} maxLength={60} autoComplete="off" className={inputClass} />
              </div>
            </div>
          )}

          {conSede && (
            <div>
              <label htmlFor="dipendente-sede" className={labelClass}>Sede</label>
              <select id="dipendente-sede" value={sede} onChange={(e) => setSede(e.target.value)} className={inputClass}>
                {(sedi || []).map((s) => (
                  <option key={valoreSede(s.id)} value={valoreSede(s.id)}>{s.nome}</option>
                ))}
              </select>
            </div>
          )}

          {conPassword && (
            <div>
              <label htmlFor="dipendente-password" className={labelClass}>Password</label>
              <div className="flex gap-2">
                <div className="relative flex-1">
                  <input
                    id="dipendente-password"
                    type={mostra ? 'text' : 'password'}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    autoComplete="new-password"
                    className={`${inputClass} pr-9`}
                  />
                  <button
                    type="button"
                    onClick={() => setMostra((v) => !v)}
                    aria-label={mostra ? 'Nascondi la password' : 'Mostra la password'}
                    className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200"
                  >
                    {mostra ? <EyeOff className="w-4 h-4" aria-hidden="true" /> : <Eye className="w-4 h-4" aria-hidden="true" />}
                  </button>
                </div>
                <button
                  type="button"
                  onClick={() => { setPassword(generaPassword()); setMostra(true); }}
                  className="inline-flex items-center gap-1 px-3 py-2 text-sm font-medium rounded-lg border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-800"
                >
                  <Wand2 className="w-4 h-4" aria-hidden="true" />
                  Genera
                </button>
              </div>
              <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">{REGOLA_PASSWORD}</p>
            </div>
          )}

          {conSezioni && (
            <fieldset>
              <legend className={labelClass}>Sezioni che può usare</legend>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-1.5">
                {elencoSezioni.map((codice) => (
                  <label key={codice} className="flex items-start gap-2 p-2 rounded-lg border border-gray-200 dark:border-gray-700 text-sm text-gray-800 dark:text-gray-200 cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-800">
                    <input
                      type="checkbox"
                      checked={sezioni.includes(codice)}
                      onChange={() => cambiaSezione(codice)}
                      className="mt-0.5 rounded border-gray-300 dark:border-gray-600 text-indigo-600 focus:ring-indigo-500"
                    />
                    <span>{ETICHETTE_SEZIONE[codice as CodiceSezione] || codice}</span>
                  </label>
                ))}
              </div>
            </fieldset>
          )}
        </div>

        <div className="mt-5 flex justify-end gap-2">
          <button type="button" onClick={onClose} disabled={salvataggio} className="px-3 py-1.5 text-sm font-medium text-gray-600 dark:text-gray-300 bg-gray-100 dark:bg-gray-800 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-700 disabled:opacity-50">
            Annulla
          </button>
          <button type="submit" disabled={salvataggio} className="px-3 py-1.5 text-sm font-medium text-white bg-indigo-600 rounded-lg hover:bg-indigo-700 disabled:opacity-50">
            {salvataggio ? 'Salvataggio…' : PULSANTI[modo]}
          </button>
        </div>
      </form>
    </div>
  );
};

export default ModuloDipendente;
